import type { FramePrediction } from "../contracts";
import { toPresentationUs } from "../../utils/time";

const MAX_GAP_FRAMES = 4;
const MAX_GAP_US = 200_000;
interface FrameLink {
  end: number;
  next?: number;
  missing: boolean;
}

function after(values: readonly number[], time: number): number {
  let low = 0;
  let high = values.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (values[middle]! <= time) low = middle + 1;
    else high = middle;
  }
  return low;
}

/** Decoded timing evidence from successful final intervals in one logical GT cluster. */
export class DecodedGapEvidence {
  private frames = new Map<number, FrameLink | null>();
  private domains: { start: number; end: number }[] = [];
  private indexed?: { times: number[]; domains: { start: number; end: number }[]; starts: number[] };

  add(frames: readonly FramePrediction[], start: number, end: number): void {
    this.indexed = undefined;
    this.domains.push({ start: toPresentationUs(start), end: toPresentationUs(end) });
    for (let i = 0; i < frames.length; i++) {
      const frame = frames[i]!;
      const timestamp = toPresentationUs(frame.timestamp);
      const frameEnd = toPresentationUs(frame.timestamp + frame.duration);
      const next = frames[i + 1] ? toPresentationUs(frames[i + 1]!.timestamp) : undefined;
      if (!Number.isFinite(timestamp)) continue;
      if (!Number.isFinite(frameEnd) || frameEnd <= timestamp || (next !== undefined && next <= timestamp)) {
        this.frames.set(timestamp, null);
        continue;
      }
      const previous = this.frames.get(timestamp);
      const missing = frame.method === "unavailable";
      if (
        previous === null ||
        (previous &&
          (previous.end !== frameEnd ||
            previous.missing !== missing ||
            (previous.next !== undefined && next !== undefined && previous.next !== next)))
      ) {
        this.frames.set(timestamp, null);
      } else {
        // Identical shared endpoints join adjacent interval enumerations without
        // counting the same presented frame twice. Merely touching lists do not.
        this.frames.set(timestamp, { end: frameEnd, missing, next: previous?.next ?? next });
      }
    }
  }

  /** Return the proven missing-frame count, or nothing when the gap is too large or not fully decoded. */
  count(start: number, end: number): number | undefined {
    start = toPresentationUs(start);
    end = toPresentationUs(end);
    if (!(start < end) || end - start > MAX_GAP_US) return undefined;
    if (!this.indexed) {
      const domains: { start: number; end: number }[] = [];
      for (const domain of this.domains.sort((a, b) => a.start - b.start)) {
        const previous = domains.at(-1);
        if (previous && domain.start <= previous.end) previous.end = Math.max(previous.end, domain.end);
        else domains.push({ ...domain });
      }
      this.indexed = {
        times: [...this.frames.keys()].sort((a, b) => a - b),
        domains,
        starts: domains.map((domain) => domain.start),
      };
    }
    const { times, domains, starts } = this.indexed;
    const domain = domains[after(starts, start) - 1];
    if (!domain || domain.end < end) return undefined;
    let position = after(times, start) - 1;
    let timestamp = times[position];
    for (let count = 1; count <= MAX_GAP_FRAMES && timestamp !== undefined; count++) {
      const frame = this.frames.get(timestamp);
      if (!frame?.missing) return undefined;
      const nextKnown = times[position + 1];
      const frameEnd = Math.min(frame.end, frame.next ?? Infinity, nextKnown ?? Infinity);
      if (!(frameEnd > start)) return undefined;
      if (end <= frameEnd) return count;
      // Both temporal continuity and an actual enumerated successor are needed.
      if (frame.next === undefined || frameEnd < frame.next || frame.next !== nextKnown) return undefined;
      start = frameEnd;
      timestamp = frame.next;
      position++;
    }
    return undefined;
  }
}
